import { Button, Modal, Toast } from '@byecode/ui'
import type { ApplicationSettingAuthentication, DevicePreviewEnum } from '@lighthouse/core'
import { isMiniprogram, isWechatBrowser } from '@lighthouse/tools'
import React, { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { AgreementCheckbox, ConfirmContent } from '../AgreementCheckbox'
import LoginBox from '../LoginBox'
import * as CM from '../styles'
import type { AppLoginMode } from '../types'
import * as SC from './styles'

interface HomeProps {
    authentication: ApplicationSettingAuthentication
    language: string
    previewType?: DevicePreviewEnum
    enabledLoginMethodsCount: number
    isShowWeChatLogin?: boolean
    isWxOfficialAccount?: boolean
    disableEvent?: boolean
    agreedToTerms?: boolean
    onChangeLanguage: (v: string) => void
    onWeChatLogin?: () => void
    onHomeBack?: () => void
    onChangeMode: (v: AppLoginMode) => void
    onAgreedToTermsChange?: (agreed: boolean) => void
}

export const Home: React.FunctionComponent<HomeProps> = ({
    authentication,
    language,
    previewType,
    enabledLoginMethodsCount,
    isShowWeChatLogin,
    isWxOfficialAccount,
    disableEvent,
    agreedToTerms: propAgreedToTerms = false,
    onChangeLanguage,
    onChangeMode,
    onHomeBack,
    onWeChatLogin,
    onAgreedToTermsChange
}) => {
    const { login, name, logo, describe } = authentication
    const { phone, weChat, google, email, requireAgree } = login
    const { t } = useTranslation()

    // 使用外部传入的协议同意状态
    const agreedToTerms = propAgreedToTerms

    // 检查是否需要协议校验
    const needsAgreementValidation = !!requireAgree?.enable

    // 协议校验函数
    const validateAgreement = useCallback(async () => {
        if (!needsAgreementValidation) {
            return true
        }
        if ((enabledLoginMethodsCount > 1 || isShowWeChatLogin) && !agreedToTerms) {
            // 检查协议同意状态
            if (requireAgree?.enable && !agreedToTerms) {
                const isConfirm = await Modal.confirm({
                    title: '用户协议及隐私条款',
                    content: <ConfirmContent authentication={authentication} />,
                    okText: '同意',
                    okStatus: 'primary',
                    okProps: { primaryColor: 'var(--color-app-main)' }
                })

                if (!isConfirm) {
                    return false
                }

                onAgreedToTermsChange?.(true)
                return true
            }
            return false
        }
        return true
    }, [
        needsAgreementValidation,
        enabledLoginMethodsCount,
        isShowWeChatLogin,
        agreedToTerms,
        requireAgree?.enable,
        authentication,
        onAgreedToTermsChange
    ])

    const isNoLoginWay = useMemo(
        () => !phone.isOpened && !weChat.isOpened && !google.isOpened && !email.isOpened,
        [email.isOpened, google.isOpened, phone.isOpened, weChat.isOpened]
    )

    if (isNoLoginWay) {
        return (
            <LoginBox
                name={name}
                logo={logo}
                describe={describe}
                language={language}
                previewType={previewType}
                onChangeLanguage={onChangeLanguage}
                customBtn={
                    <Button
                        style={{
                            height: 94,
                            background: 'var(--color-gray-100)',
                            color: 'var(--color-gray-400)',
                            marginTop: 26,
                            border: 'none',
                            cursor: 'not-allowed'
                        }}
                        radius={10}
                        block
                    >
                        {t('noNeedLogin')}
                    </Button>
                }
            />
        )
    }

    return (
        <LoginBox
            name={name}
            logo={logo}
            describe={describe}
            hideBackBtn
            language={language}
            previewType={previewType}
            onChangeLanguage={onChangeLanguage}
        >
            <SC.LoginWay>
                {isShowWeChatLogin && (
                    <SC.LoginWayItem
                        style={{ backgroundColor: '#00CA45', color: 'var(--color-white)', border: 'none' }}
                        onClick={async () => {
                            if (!(await validateAgreement())) {
                                return
                            }

                            if (disableEvent) {
                                return
                            }
                            if (isMiniprogram()) {
                                onWeChatLogin?.()
                                return
                            }
                            if (isWxOfficialAccount) {
                                //  在微信浏览器则一键登陆
                                if (isWechatBrowser()) {
                                    onWeChatLogin?.()
                                    return
                                }
                                weChat.isOpenedH5QrcodeLogin
                                    ? onChangeMode('weChatLogin')
                                    : Toast.warning(t('pleaseWeChatLoginInWeChatBrowser'))
                            }
                        }}
                    >
                        {/* <CM.Icon type="WeChatLogoIconLibrary" color="var(--color-green-500)" size={20} className="icon" /> */}
                        <SC.LoginWayTitle> {t('wechatLogin')}</SC.LoginWayTitle>
                    </SC.LoginWayItem>
                )}
                {phone.isOpened && (
                    <SC.LoginWayItem
                        onClick={async () => {
                            if (!(await validateAgreement())) {
                                return
                            }
                            onChangeMode('mobileLogin')
                        }}
                    >
                        {/* <CM.Icon type="SmartphoneIconLibrary" color="var(--color-blue-500)" size={20} className="icon" /> */}
                        <SC.LoginWayTitle> {t('mobileLogin')}</SC.LoginWayTitle>
                    </SC.LoginWayItem>
                )}
                {email.isOpened && (
                    <SC.LoginWayItem
                        onClick={async () => {
                            if (!(await validateAgreement())) {
                                return
                            }
                            onChangeMode('emailLogin')
                        }}
                    >
                        {/* <CM.Icon type="LetterIconLibrary" color="var(--color-theme-7)" size={20} className="icon" /> */}
                        <SC.LoginWayTitle> {t('emailLogin')}</SC.LoginWayTitle>
                    </SC.LoginWayItem>
                )}

                {/* 只有当有多种登录方式且开启协议要求时才显示 */}
                {(enabledLoginMethodsCount > 1 || isShowWeChatLogin) && needsAgreementValidation && onAgreedToTermsChange && (
                    <AgreementCheckbox
                        disableMargin
                        authentication={authentication}
                        checked={agreedToTerms}
                        onChange={onAgreedToTermsChange}
                    />
                )}

                <SC.LoginWayItem
                    onClick={onHomeBack}
                    style={{ backgroundColor: 'var(--color-app-main-tint)', borderStyle: 'none', color: 'var(--color-app-main)' }}
                >
                    <SC.LoginWayTitle>{t('goHome')}</SC.LoginWayTitle>
                </SC.LoginWayItem>
            </SC.LoginWay>
        </LoginBox>
    )
}
